import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { empleadosService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
    Card,
    Table,
    Form,
    Button,
    Pagination,
    InputGroup,
    Row,
    Col,
    Badge,
} from "react-bootstrap";
import { FaSearch, FaEye, FaBell, FaUpload, FaArrowLeft, FaTrash, FaPaperPlane, FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";

const PAGE_SIZE = 8;

// Función para formatear fechas a DD/MM/YYYY
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Si no es una fecha válida, devolver el string original
    return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Función para convertir texto a valores booleanos para la base de datos
const parseBooleanValue = (value) => {
    if (value === 'Sí' || value === 1 || value === '1') {
        return 1;
    }
    return 0;
};

// Función para convertir valores bigint de campos de estado a texto legible
// Mapeo de valores: 1 = 'Sí', 2 = 'No', 3 = 'Enviado'
// Esta función maneja los campos de estado de la base de datos
const formatStatusField = (value) => {
    if (value == 1) {
        return 'Sí';
    } else if (value == 2) {
        return 'No';
    } else if (value == 3) {
        return 'Enviado';
    }
};

// Función para obtener la clase CSS según el valor del campo de estado
// Aplica colores y estilos diferentes para cada estado
// Verde para 'Sí', Rojo para 'No', Azul para 'Enviado'
const getStatusFieldClass = (value) => {
    if (value == 1) {
        return 'text-success fw-bold'; // Verde y negrita para 'Sí'
    } else if (value == 2) {
        return 'text-danger fw-bold'; // Rojo y negrita para 'No'
    } else if (value == 3) {
        return 'text-primary fw-bold'; // Azul y negrita para 'Enviado'
    }
};

// Función para procesar campos de estado antes de enviar al backend
// Convierte valores null/undefined a 3 por defecto, mantiene otros valores intactos
// Esta función asegura que los campos bigint nunca se envíen como null
const processStatusFieldForBackend = (value) => {
    if (value === null || value === undefined) {
        return 3; // Convertir null/undefined a 3 por defecto (se mostrará como "Enviado")
    }
    return value; // Mantener otros valores (0, 1, 2, 3) intactos
};

// Función para convertir fechas al formato YYYY-MM-DD para el input type="date"
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
};

// Función para formatear el estado del empleado
// Si ESTADO es 1, muestra "Validado", en caso contrario "No Validado"
const formatEmployeeStatus = (estado) => {
    return estado == 1 ? "Validado" : "No Validado";
};

// Función para obtener la clase CSS del estado del empleado
const getEmployeeStatusClass = (estado) => {
    return estado == 1 ? "text-success fw-bold" : "text-warning fw-bold";
};

const EmpleadoDetail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const empleadoId = searchParams.get("empleado_id");

    // Estados para el componente

    const [empleados, setEmpleados] = useState([]);
    const [empleadoDetalle, setEmpleadoDetalle] = useState(null);
    const [empleadoEdit, setEmpleadoEdit] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [orderBy, setOrderBy] = useState(0);
    const [loading, setLoading] = useState(false);
    const [departamentos, setDepartamentos] = useState([]);
    const [contratosHoras, setContratosHoras] = useState([]);
    const [relacionesLaborales, setRelacionesLaborales] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        pages: 1,
    });

    // Estado para almacenar los tipos de documentos obtenidos del backend
    const [tiposDocumentos, setTiposDocumentos] = useState([]);

    // Estado para controlar la visibilidad de documentos (dinámico basado en tiposDocumentos)
    const [documentosVisibles, setDocumentosVisibles] = useState({});

    // Estado para rastrear las tarjetas de documentos seleccionadas (dinámico basado en tiposDocumentos)
    const [documentosSeleccionados, setDocumentosSeleccionados] = useState({});

    // Estados para la modal de subida de archivos
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [currentDocumentType, setCurrentDocumentType] = useState('');
    const [validityDate, setValidityDate] = useState('');
    // const [showValidityModal, setShowValidityModal] = useState(false);

    // Estados para el seguimiento factorial
    const [factoresSeguimiento, setFactoresSeguimiento] = useState([]);
    const [showFactoresDropdown, setShowFactoresDropdown] = useState(false);
    const [tiposSeguimientoFactorial, setTiposSeguimientoFactorial] = useState([]);

    // Efecto para cerrar el dropdown cuando se haga clic fuera de él
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showFactoresDropdown && !event.target.closest('.factores-dropdown-container')) {
                setShowFactoresDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFactoresDropdown]);

    // Efecto para redirigir automáticamente a trabajadores a su perfil
    useEffect(() => {
        // Si no hay empleado_id y el usuario es un trabajador (no admin), redirigir a su perfil
        if (!empleadoId && user && user.ROL !== 'admin') {
            // Intentar obtener el ID del usuario de diferentes formas
            const userId = user.ID || user.id || user.empleado_id || user.empleadoId;
            console.log('Redirección automática - ID del usuario:', userId);

            if (userId) {
                navigate(`/empleado-detail?empleado_id=${userId}`);
            } else {
                console.error('No se pudo obtener el ID del usuario para redirección automática');
                toast.error('Error: No se pudo obtener el ID del usuario');
            }
        }

        // Si hay empleado_id y el usuario es un trabajador, verificar que sea su propio ID
        if (empleadoId && user && user.ROL !== 'admin') {
            const userId = user.ID || user.id || user.empleado_id || user.empleadoId;

            // Si el empleado_id de la URL es diferente al ID del usuario logueado, redirigir
            if (userId && empleadoId !== userId.toString()) {
                console.log('Trabajador intentando acceder a ID diferente:', empleadoId, 'vs su ID:', userId);
                toast.warning('Solo puedes acceder a tu propio perfil');
                navigate(`/empleado-detail?empleado_id=${userId}`);
                return;
            }
        }
    }, [empleadoId, user, navigate]);

    useEffect(() => {
        if (empleadoId) {
            fetchEmpleadoDetalle();
            fetchTiposDocumentos();
        }
        // eslint-disable-next-line
    }, [page, orderBy, search, empleadoId]);

    // Efecto separado para cargar departamentos, contratos de horas y relaciones laborales cuando se tenga el detalle del empleado
    useEffect(() => {
        if (empleadoDetalle) {
            fetchDepartamentos();
            fetchContratosHoras();
            fetchRelacionesLaborales();
            // Cargar tipos de seguimiento factorial después de tener el detalle del empleado
            fetchTiposSeguimientoFactorial(empleadoId);
        }
    }, [empleadoDetalle, empleadoId]);

    // Efecto para monitorear cambios en el estado del empleado
    useEffect(() => {
        if (empleadoDetalle) {
            console.log('=== empleadoDetalle.ESTADO cambió ===');
            console.log('Nuevo ESTADO:', empleadoDetalle.ESTADO);
            console.log('Estado formateado:', formatEmployeeStatus(empleadoDetalle.ESTADO));
        }
    }, [empleadoDetalle?.ESTADO]);

    // Efecto para monitorear cambios en factoresSeguimiento
    useEffect(() => {
        console.log('=== factoresSeguimiento cambió ===');
        console.log('Nuevo factoresSeguimiento:', factoresSeguimiento);
        console.log('Cantidad de factores seleccionados:', factoresSeguimiento.length);
    }, [factoresSeguimiento]);

    // Función para obtener los tipos de seguimiento factorial
    const fetchTiposSeguimientoFactorial = async (empleadoId) => {
        try {
            console.log('=== EJECUTANDO fetchTiposSeguimientoFactorial ===');
            console.log('empleadoId recibido:', empleadoId);

            if (!empleadoId) {
                console.log('No hay empleadoId disponible para cargar tipos de seguimiento factorial');
                return;
            }

            const res = await empleadosService.getSeguimientoFactorialTipos(empleadoId);
            if (res.data.success && res.data.tipos) {
                console.log('Tipos de seguimiento factorial obtenidos:', res.data.tipos);
                setTiposSeguimientoFactorial(res.data.tipos);

                // Inicializar factoresSeguimiento con los factores que ya están seleccionados
                const factoresSeleccionados = res.data.tipos
                    .filter(tipo => tipo.seleccionado === 1 || tipo.seleccionado === true)
                    .map(tipo => tipo.ID);

                console.log('Factores ya seleccionados:', factoresSeleccionados);
                setFactoresSeguimiento(factoresSeleccionados);

                console.log('Estado factoresSeguimiento inicializado');
            }
        } catch (err) {
            console.error("Error al cargar tipos de seguimiento factorial:", err);
            // No mostrar error al usuario ya que no es crítico
        }
    };

    const fetchEmpleados = async () => {
        setLoading(true);
        try {
            const res = await empleadosService.buscar({
                search,
                page,
                limit: PAGE_SIZE,
                orderBy,
            });
            setEmpleados(res.data.empleados);
            if (res.data.pagination) {
                setPagination(res.data.pagination);
            } else {
                setPagination((prev) => ({
                    ...prev,
                    total: res.data.empleados.length,
                    pages: 1,
                }));
            }
        } catch (err) {
            setEmpleados([]);
            setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
        } finally {
            setLoading(false);
        }
    };

    const fetchEmpleadoDetalle = async () => {
        setLoading(true);
        try {
            const res = await empleadosService.getDetail(empleadoId);
            if (res.data.empleado) {
                setEmpleadoDetalle(res.data.empleado);
            }
        } catch (err) {
            toast.error("Error al cargar el detalle del empleado");
        } finally {
            setLoading(false);
        }
    };

    // Función para verificar solo la validación del empleado sin recargar documentos
    const checkEmployeeValidationOnly = async () => {
        try {
            console.log('=== EJECUTANDO checkEmployeeValidationOnly ===');
            console.log('empleadoId:', empleadoId);
            console.log('Estado actual del empleado:', empleadoDetalle?.ESTADO);

            const validationResponse = await empleadosService.checkEmployeeValidation(empleadoId);
            console.log('Respuesta de validación:', validationResponse);
            console.log('estadoActual:', validationResponse.data?.estadoActual);

            if (validationResponse.data.success) {
                console.log('Estado de validación del empleado:', validationResponse.data);

                // Actualizar el campo ESTADO del empleado con el estado actual
                const estadoActual = validationResponse.data.estadoActual;
                console.log('Estado actual obtenido:', estadoActual);

                setEmpleadoDetalle(prev => {
                    console.log('Estado anterior del empleado (prev):', prev);
                    const nuevoEstado = {
                        ...prev,
                        ESTADO: estadoActual
                    };
                    console.log('Nuevo estado del empleado:', nuevoEstado);
                    return nuevoEstado;
                });

                toast.info(`Estado del empleado actualizado: ${estadoActual == 1 ? 'Validado' : 'No Validado'}`);
            } else {
                console.log('La respuesta de validación no fue exitosa');
            }
        } catch (error) {
            console.error('Error verificando validación del empleado:', error);
            console.error('Detalles del error:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });
            // No mostrar error al usuario ya que no es crítico
        }
    };

    const fetchDepartamentos = async () => {
        try {
            // Obtener el valor actual del DPTO_FUNCION del empleado
            const dptoFuncion = empleadoDetalle?.DPTO_FUNCION;
            const res = await empleadosService.getDepartamentos(dptoFuncion);
            if (res.data.departamentos) {
                setDepartamentos(res.data.departamentos);
            }
        } catch (err) {
            console.error("Error al cargar departamentos:", err);
            toast.error("Error al cargar los departamentos");
        }
    };

    const fetchContratosHoras = async () => {
        try {
            // Obtener el valor actual del CONTRATO_HORAS del empleado
            const contratoHoras = empleadoDetalle?.CONTRATO_HORAS;
            const res = await empleadosService.getContratoHoras(contratoHoras);
            if (res.data.contratosHoras) {
                setContratosHoras(res.data.contratosHoras);
            }
        } catch (err) {
            console.error("Error al cargar contratos de horas:", err);
            toast.error("Error al cargar los contratos de horas");
        }
    };

    const fetchRelacionesLaborales = async () => {
        try {
            // Obtener el valor actual del RELACION_LABORAL del empleado
            const relacionLaboral = empleadoDetalle?.RELACION_LABORAL;
            const res = await empleadosService.getRelacionLaboral(relacionLaboral);
            if (res.data.relacionesLaborales) {
                setRelacionesLaborales(res.data.relacionesLaborales);
            }
        } catch (err) {
            console.error("Error al cargar relaciones laborales:", err);
            toast.error("Error al cargar las relaciones laborales");
        }
    };

    // Función para obtener tipos de documentos desde el procedimiento almacenado
    const fetchTiposDocumentos = async () => {
        try {
            const res = await empleadosService.getDocumentTypes();
            if (res.data.success && res.data.documentTypes) {
                const tipos = res.data.documentTypes;
                setTiposDocumentos(tipos);

                // Inicializar estados dinámicamente basados en los tipos obtenidos
                const visibles = {};
                const seleccionados = {};

                tipos.forEach(tipo => {
                    visibles[tipo.ID] = false;
                    seleccionados[tipo.ID] = false;
                });

                setDocumentosVisibles(visibles);
                setDocumentosSeleccionados(seleccionados);

                // Si tenemos un empleadoId, obtener sus documentos asociados
                if (empleadoId) {
                    await fetchEmployeeDocuments(tipos);
                }
            }
        } catch (err) {
            console.error("Error al cargar tipos de documentos:", err);
            toast.error("Error al cargar los tipos de documentos");
        }
    };

    // Función para obtener documentos asociados al empleado y marcar obligatorios
    const fetchEmployeeDocuments = async (tipos) => {
        try {
            console.log('=== EJECUTANDO fetchEmployeeDocuments ===');
            console.log('empleadoId:', empleadoId);

            const res = await empleadosService.getEmployeeDocuments(empleadoId);
            console.log('Respuesta del servicio:', res);

            if (res.data.success && res.data.employeeDocuments) {
                const empleadoDocs = res.data.employeeDocuments;
                console.log('Documentos del empleado:', empleadoDocs);
                console.log('Primer documento:', empleadoDocs[0]);
                console.log('Campos del primer documento:', empleadoDocs[0] ? Object.keys(empleadoDocs[0]) : 'No hay documentos');

                // Guardar los documentos del empleado en el estado del empleado para acceder al campo 'HASTA' (fecha de validez)
                setEmpleadoDetalle(prev => {
                    console.log('Estado anterior del empleado:', prev);
                    const nuevoEstado = {
                        ...prev,
                        documentosEmpleado: empleadoDocs
                    };
                    console.log('Nuevo estado del empleado:', nuevoEstado);
                    return nuevoEstado;
                });

                // Actualizar el estado de documentosVisibles basado en los documentos obligatorios
                setDocumentosVisibles(prev => {
                    const nuevosVisibles = { ...prev };

                    empleadoDocs.forEach(doc => {
                        // Si el documento es obligatorio, marcar el checkbox
                        if (doc.OBLIGATORIO === true || doc.OBLIGATORIO === 1) {
                            nuevosVisibles[doc.DOCUMENTO_TIPO_ID] = true;
                        }
                    });

                    return nuevosVisibles;
                });
            }
        } catch (err) {
            console.error("Error al cargar documentos del empleado:", err);
            // No mostrar error al usuario ya que no es crítico
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchEmpleados();
    };

    const handleOrder = (colIdx) => {
        setOrderBy(colIdx);
        setPage(1);
    };

    const handleEdit = () => {
        setEmpleadoEdit({ ...empleadoDetalle });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEmpleadoEdit(null);
        setIsEditing(false);
    };

    // Función para guardar los cambios del empleado
    // Procesa los campos de estado convirtiendo null a 2 por defecto
    const handleSave = async () => {
        try {
            setLoading(true);

            // Preparar los datos para enviar al backend
            // Incluye conversión automática de campos de estado null a 2 por defecto
            const empleadoData = {
                NOMBRE: empleadoEdit.NOMBRE || '',
                APELLIDOS: empleadoEdit.APELLIDOS || '',
                EMAIL: empleadoEdit.EMAIL || '',
                DNI: empleadoEdit.DNI || '',
                TELEFONO: empleadoEdit.TELEFONO_DEFECTO || '',
                HORARIO: empleadoEdit.HORARIO || '',
                NUM_SS: empleadoEdit.NUM_SS || '',
                FECHA_NACIMIENTO: empleadoEdit.FECHA_NACIMIENTO === '' ? null : empleadoEdit.FECHA_NACIMIENTO,
                DEPARTAMENTO: empleadoEdit.DPTO_FUNCION === '' ? null : empleadoEdit.DPTO_FUNCION,
                CONTRATO_HORAS: empleadoEdit.CONTRATO_HORAS === '' ? null : empleadoEdit.CONTRATO_HORAS,
                RELACION_LABORAL: empleadoEdit.RELACION_LABORAL === '' ? null : empleadoEdit.RELACION_LABORAL,
                FECHA_ALTA: empleadoEdit.FECHA_ALTA === '' ? null : empleadoEdit.FECHA_ALTA,
                // Campo de acceso a la plataforma
                ACCESO_PLATAFORMA: empleadoEdit.ACCESO_PLATAFORMA || 0,
                // Campo de estado activo del empleado
                ACTIVO: empleadoEdit.ACTIVO !== undefined ? empleadoEdit.ACTIVO : 1,
                // Enviar notificaciones al empleado
                ENVIAR_NOTIFICACIONES: empleadoEdit.ENVIAR_NOTIFICACIONES !== undefined ? empleadoEdit.ENVIAR_NOTIFICACIONES : 0,
                // Campos de estado: convertir null a 2 por defecto para evitar errores en BBDD
                // Los campos bigint no pueden ser null, por lo que se convierten a 2 ('No')
                PROTECCION_DATOS: processStatusFieldForBackend(empleadoEdit.PROT_DATOS),
                POLITICA_RRLL: processStatusFieldForBackend(empleadoEdit.POLITICA_RR_LL),
                ACOSO: processStatusFieldForBackend(empleadoEdit.ACOSO),
                CONFIDENCIALIDAD: processStatusFieldForBackend(empleadoEdit.CONFIDENCIALIDAD)
            };

            // Llamar al backend para actualizar el empleado
            const response = await empleadosService.update(empleadoId, empleadoData);

            if (response.data.success) {
                setEmpleadoDetalle(empleadoEdit);
                setEmpleadoEdit(null);
                setIsEditing(false);
                toast.success("Empleado actualizado correctamente");
                // Recargar la página para reflejar los cambios
                window.location.reload();
            } else {
                toast.error(response.data.message || "Error al actualizar el empleado");
            }
        } catch (error) {
            console.error('Error actualizando empleado:', error);
            toast.error(
                error.response?.data?.message ||
                "Error al actualizar el empleado. Verifique la conexión con la base de datos."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEmpleadoEdit(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Funciones para manejar acciones de documentos
    const handleUploadDocument = (documentType) => {
        setCurrentDocumentType(documentType);

        // Verificar si el documento no requiere renovación
        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === documentType);
        const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

        if (esDocumentoSinRenovacion) {
            // Si el documento no requiere renovación, establecer fecha como null
            setValidityDate(null);
        } else if (tipoDocumento && tipoDocumento.MESES_HASTA_PROX_RENOVACION) {
            // Calcular automáticamente la fecha de validez basándose en MESES_HASTA_PROX_RENOVACION
            const fechaActual = new Date();
            const fechaValidez = new Date(fechaActual);
            fechaValidez.setMonth(fechaValidez.getMonth() + tipoDocumento.MESES_HASTA_PROX_RENOVACION);

            // Formatear la fecha para el input type="date" (YYYY-MM-DD)
            const fechaFormateada = fechaValidez.toISOString().split('T')[0];
            setValidityDate(fechaFormateada);
        } else {
            // Si no hay meses de renovación, limpiar la fecha
            setValidityDate('');
        }

        setShowUploadModal(true);
    };

    const handleDeleteDocument = async (documentType) => {
        try {
            // Confirmar antes de eliminar
            if (!window.confirm('¿Estás seguro de que quieres eliminar este fichero? Esta acción no se puede deshacer.')) {
                return;
            }

            setLoading(true);

            // Llamar al servicio para eliminar el fichero del documento
            const response = await empleadosService.deleteDocumentFile(empleadoId, documentType);

            if (response.data.success) {
                toast.success('Fichero eliminado correctamente');

                // Recargar los datos del empleado para actualizar la interfaz
                await fetchEmpleadoDetalle();

                // También recargar los documentos del empleado para actualizar las tarjetas
                if (tiposDocumentos.length > 0) {
                    await fetchEmployeeDocuments(tiposDocumentos);
                }

                // Verificar automáticamente si el empleado sigue validado después de eliminar el documento
                try {
                    await checkEmployeeValidationOnly();
                } catch (error) {
                    console.error('Error verificando validación del empleado:', error);
                }
            } else {
                toast.error(response.data.message || 'Error al eliminar el fichero');
            }
        } catch (error) {
            console.error('Error eliminando fichero del documento:', error);
            toast.error(
                error.response?.data?.message ||
                'Error al eliminar el fichero. Verifique la conexión con la base de datos.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSendDocument = async (documentType) => {
        try {
            // Si no es VisualMax, enviar la plantilla de autónomo
            if (parseInt(empleadoDetalle?.RELACION_LABORAL) != 1) {
                // Si es autónomo, obtener la plantilla de autónomo
                const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === documentType);
                if (tipoDocumento?.PLANTILLA_AUTONOMO) {
                    console.log('Empleado autónomo - Usando plantilla:', tipoDocumento.PLANTILLA_AUTONOMO);

                    // Construir la ruta completa al archivo de plantilla
                    const rutaPlantilla = `/api/plantillas/autonomos/${tipoDocumento.PLANTILLA_AUTONOMO}`;
                    console.log('Ruta de la plantilla:', rutaPlantilla);

                    // Procesar y enviar la plantilla modificada por email
                    await procesarYEnviarPlantilla(rutaPlantilla, tipoDocumento.NOMBRE, documentType);

                } else {
                    toast.warning('No se encontró plantilla de autónomo para este documento');
                }
            } else {
                // Si no es autónomo, obtener la plantilla de VisualMax
                const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === documentType);
                if (tipoDocumento?.PLANTILLA_VISUALMAX) {
                    console.log('Empleado no autónomo - Usando plantilla:', tipoDocumento.PLANTILLA_VISUALMAX);

                    // Construir la ruta completa al archivo de plantilla
                    const rutaPlantilla = `/api/plantillas/visualmax/${tipoDocumento.PLANTILLA_VISUALMAX}`;
                    console.log('Ruta de la plantilla:', rutaPlantilla);

                    // Procesar y descargar la plantilla modificada
                    await procesarYEnviarPlantilla(rutaPlantilla, tipoDocumento.NOMBRE, tipoDocumento.ID);

                } else {
                    toast.warning('No se encontró plantilla de VisualMax para este documento');
                }
            }
        } catch (error) {
            console.error('Error al procesar el envío del documento:', error);
            toast.error('Error al procesar el envío del documento');
        }
    };

    // Función para procesar y enviar la plantilla modificada por email
    const procesarYEnviarPlantilla = async (rutaPlantilla, nombreDocumento, documentTypeId) => {
        try {
            toast.info('Procesando plantilla, enviando por email y preparando descarga...');

            // Obtener el nombre y apellidos del empleado
            const nombreEmpleado = empleadoDetalle?.NOMBRE || '';
            const apellidosEmpleado = empleadoDetalle?.APELLIDOS || '';
            const DNI = empleadoDetalle?.DNI || '';
            const nombreCompleto = `${nombreEmpleado} ${apellidosEmpleado}`.trim();


            const departamentoTrabajador = departamentos.find(departamento => parseInt(departamento.ID) === parseInt(empleadoDetalle.DPTO_FUNCION));
            const nombreDepartamento = departamentoTrabajador?.NOMBRE || '';

            if (!nombreCompleto) {
                toast.error('No se pudo obtener el nombre completo del empleado');
                return;
            }

            // Leer el archivo PDF de la plantilla con autenticación
            const token = localStorage.getItem('token');
            const response = await fetch(rutaPlantilla, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Error al leer la plantilla: ${response.status}`);
            }

            const pdfBytes = await response.arrayBuffer();

            // Importar pdf-lib dinámicamente
            const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');

            // Cargar el documento PDF
            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pages = pdfDoc.getPages();

            // Obtener la fuente estándar
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            // Procesar cada página del PDF
            pages.forEach((page, pageIndex) => {
                const { width, height } = page.getSize();

                // Definir posiciones específicas según el tipo de documento
                let posicionesDocumento = [];

                if (parseInt(empleadoDetalle.RELACION_LABORAL) != 1) {
                    switch (parseInt(documentTypeId)) {
                        case 1: // Renuncia RM


                            posicionesDocumento = [
                                { x: 210, y: height - 144, size: 12, label: nombreCompleto },
                                { x: 95, y: height - 170, size: 12, label: DNI },
                                { x: 185, y: height - 520, size: 10, label: nombreCompleto },
                            ];
                            break;

                        case 2:

                            break;

                        case 3: // Entrega PRL
                            posicionesDocumento = [
                                { x: 330, y: height - 160, size: 8, label: DNI },
                                { x: 375, y: height - 180, size: 8, label: nombreCompleto },
                                { x: 355, y: height - 210, size: 8, label: nombreDepartamento },
                                { x: 100, y: height - 610, size: 12, label: nombreCompleto },
                            ];
                            break;

                        case 4: // PROTECCION INDIVIDUAL
                            posicionesDocumento = [
                                { x: 90, y: height - 145, size: 8, label: DNI },
                                { x: 135, y: height - 163, size: 8, label: nombreCompleto },
                                { x: 120, y: height - 195, size: 8, label: nombreDepartamento },
                            ];
                            break;

                        case 5: // Formulario de departamento
                            posicionesDocumento = [
                                { x: 335, y: height - 152, size: 10, label: DNI },
                                { x: 400, y: height - 172, size: 10, label: nombreCompleto },
                                { x: 375, y: height - 217, size: 10, label: nombreDepartamento },
                            ];
                            break;

                        case 6: // Documento de horarios

                            break;

                        case 7: // Formulario de contacto

                            break;

                        case 8: // Documento de formación

                            break;

                        case 9: // Formulario de evaluación

                            break;

                        case 10: // Documento de incidencias

                            break;

                        case 11: // Formulario de solicitud

                            break;

                        case 12: // Documento de finalización
                            posicionesDocumento = [
                                { x: 100, y: height - 230, size: 10, label: nombreCompleto },
                                { x: 360, y: height - 230, size: 10, label: DNI },
                            ];
                            break;

                        default: // Posiciones genéricas por defecto

                            break;
                    }
                } else {
                    switch (parseInt(documentTypeId)) {
                        case 1: // Renuncia RM


                            posicionesDocumento = [
                                { x: 150, y: height - 190, size: 12, label: nombreCompleto },
                                { x: 100, y: height - 215, size: 12, label: DNI },
                                { x: 135, y: height - 590, size: 10, label: nombreCompleto },
                            ];
                            break;

                        case 2:

                            break;

                        case 3: // Entrega PRL
                            posicionesDocumento = [
                                { x: 330, y: height - 145, size: 8, label: DNI },
                                { x: 375, y: height - 163, size: 8, label: nombreCompleto },
                                { x: 355, y: height - 195, size: 8, label: nombreDepartamento },
                                { x: 110, y: height - 567, size: 12, label: nombreCompleto },
                            ];
                            break;

                        case 4: // PROTECCION INDIVIDUAL
                            posicionesDocumento = [
                                { x: 335, y: height - 165, size: 10, label: DNI },
                                { x: 400, y: height - 185, size: 10, label: nombreCompleto },
                                { x: 375, y: height - 230, size: 10, label: nombreDepartamento },
                            ];
                            break;

                        case 5: // Formulario de departamento
                            posicionesDocumento = [
                                { x: 335, y: height - 152, size: 10, label: DNI },
                                { x: 400, y: height - 172, size: 10, label: nombreCompleto },
                                { x: 375, y: height - 217, size: 10, label: nombreDepartamento },
                            ];
                            break;

                        case 6: // Documento de horarios

                            break;

                        case 7: // Formulario de contacto

                            break;

                        case 8: // Documento de formación

                            break;

                        case 9: // Formulario de evaluación

                            break;

                        case 10: // Documento de incidencias

                            break;

                        case 11: // Formulario de solicitud

                            break;

                        case 12: // Documento de finalización
                            posicionesDocumento = [
                                { x: 100, y: height - 230, size: 10, label: nombreCompleto },
                                { x: 360, y: height - 230, size: 10, label: DNI },
                            ];
                            break;

                        default: // Posiciones genéricas por defecto

                            break;
                    }
                }



                // Agregar campos de nombre en posiciones específicas del tipo de documento
                posicionesDocumento.forEach((pos, index) => {
                    // Agregar etiqueta
                    page.drawText(pos.label, {
                        x: pos.x,
                        y: pos.y,
                        size: pos.size,
                        font: font,
                        color: rgb(0, 0, 0)
                    });


                });

            });

            // Generar el PDF modificado
            const modifiedPdfBytes = await pdfDoc.save();

            // Crear el blob del PDF
            const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

            // Crear FormData para enviar el PDF por email
            const formData = new FormData();
            formData.append('pdf', blob, `${nombreDocumento}_${nombreCompleto.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
            formData.append('empleadoId', empleadoId);
            formData.append('tipoDocumento', documentTypeId);
            formData.append('nombreDocumento', nombreDocumento);
            formData.append('emailEmpleado', empleadoDetalle?.EMAIL || '');
            formData.append('nombreEmpleado', nombreCompleto);

            // Enviar el PDF por email usando el servicio de empleados
            try {
                const response = await empleadosService.sendDocumentByEmail(formData);

                if (response.data.success) {
                    toast.success(`Documento ${nombreDocumento} enviado por email correctamente`);

                    //// Después de enviar por email, ofrecer la descarga
                    //const downloadLink = document.createElement('a');
                    //downloadLink.href = URL.createObjectURL(blob);
                    //downloadLink.download = `${nombreDocumento}_${nombreCompleto.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                    //document.body.appendChild(downloadLink);
                    //downloadLink.click();
                    //document.body.removeChild(downloadLink);

                    //toast.info('Documento descargado automáticamente');
                } else {
                    toast.error(response.data.message || 'Error al enviar el documento por email');
                }
            } catch (emailError) {
                console.error('Error enviando documento por email:', emailError);
                toast.error('Error al enviar el documento por email. Verifique la conexión.');

                //Si falla el envío por email, al menos permitir la descarga
                //const downloadLink = document.createElement('a');
                //downloadLink.href = URL.createObjectURL(blob);
                //downloadLink.download = `${nombreDocumento}_${nombreCompleto.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                //document.body.appendChild(downloadLink);
                //downloadLink.click();
                //document.body.removeChild(downloadLink)
                //toast.info('Documento descargado (envío por email falló)');
            }

        } catch (error) {
            console.error('Error procesando plantilla:', error);
            toast.error('Error al procesar la plantilla para envío por email');
        }
    };

    // Función para manejar la selección de archivo
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast.error('Solo se permiten archivos PDF');
                return;
            }
            if (file.size > 10 * 1024 * 1024) { // 10MB
                toast.error('El archivo no puede ser mayor a 10MB');
                return;
            }
            setSelectedFile(file);

            // Calcular automáticamente la fecha de validez basándose en MESES_HASTA_PROX_RENOVACION
            if (currentDocumentType) {
                const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

                if (esDocumentoSinRenovacion) {
                    // Si el documento no requiere renovación, establecer fecha como null
                    setValidityDate(null);
                } else if (tipoDocumento && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                    const fechaActual = new Date();
                    const fechaValidez = new Date(fechaActual);
                    fechaValidez.setMonth(fechaValidez.getMonth() + tipoDocumento.MESES_HASTA_PROX_RENOVACION);

                    // Formatear la fecha para el input type="date" (YYYY-MM-DD)
                    const fechaFormateada = fechaValidez.toISOString().split('T')[0];
                    setValidityDate(fechaFormateada);
                } else {
                    // Si MESES_HASTA_PROX_RENOVACION es null, usar la fecha de hoy
                    const fechaActual = new Date();
                    const fechaFormateada = fechaActual.toISOString().split('T')[0];
                    setValidityDate(fechaFormateada);
                }
            }
        }
    };

    // Función para subir el documento
    const handleUploadFile = async () => {
        console.log('=== INICIO SUBIDA DOCUMENTO FRONTEND ===');
        console.log('selectedFile:', selectedFile);
        console.log('validityDate:', validityDate);
        console.log('empleadoId:', empleadoId);
        console.log('currentDocumentType:', currentDocumentType);

        if (!selectedFile) {
            toast.error('Por favor selecciona un archivo');
            return;
        }

        // Verificar si el documento no requiere fecha de renovación
        const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
        const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

        console.log('Tipo documento encontrado:', tipoDocumento);
        console.log('DOCUMENTO_SIN_RENOVACION:', tipoDocumento?.DOCUMENTO_SIN_RENOVACION);
        console.log('esDocumentoSinRenovacion:', esDocumentoSinRenovacion);

        // Solo validar fecha si el documento requiere renovación
        if (!esDocumentoSinRenovacion) {
            if (!validityDate) {
                toast.error('Por favor selecciona una fecha de validez');
                return;
            }

            // Validar que la fecha de validez no sea inferior a la fecha actual
            const fechaValidez = new Date(validityDate);
            const fechaActual = new Date();
            fechaActual.setHours(0, 0, 0, 0); // Resetear a 00:00:00 para comparar solo fechas

            if (fechaValidez < fechaActual) {
                toast.error('La fecha de validez no puede ser inferior a la fecha actual');
                return;
            }
        }

        setUploadingDocument(true);
        try {
            console.log('Llamando a empleadosService.uploadDocument...');

            // Determinar la fecha a enviar: null si no requiere renovación, validityDate si la requiere
            const fechaAEnviar = esDocumentoSinRenovacion ? null : (validityDate || null);
            console.log('Fecha a enviar al backend:', fechaAEnviar);
            console.log('Tipo de fechaAEnviar:', typeof fechaAEnviar);
            console.log('fechaAEnviar === null:', fechaAEnviar === null);
            console.log('fechaAEnviar === undefined:', fechaAEnviar === undefined);
            console.log('validityDate original:', validityDate);
            console.log('esDocumentoSinRenovacion:', esDocumentoSinRenovacion);

            // Subir documento con fecha de validez en una sola llamada
            const response = await empleadosService.uploadDocument(empleadoId, currentDocumentType, selectedFile, fechaAEnviar);
            console.log('Respuesta del servidor:', response);

            if (response.data.success) {
                toast.success('Documento subido correctamente con fecha de validez');
                setShowUploadModal(false);
                setSelectedFile(null);
                setValidityDate(null);

                // Recargar los datos del empleado para actualizar la pantalla
                await fetchEmpleadoDetalle();

                // También recargar los documentos del empleado para actualizar las tarjetas
                if (tiposDocumentos.length > 0) {
                    await fetchEmployeeDocuments(tiposDocumentos);
                }

                // Verificar automáticamente si el empleado está validado después de subir el documento
                try {
                    await checkEmployeeValidationOnly();
                } catch (error) {
                    console.error('Error verificando validación del empleado:', error);
                }
            } else {
                toast.error(response.data.message || 'Error al subir el documento');
            }
        } catch (error) {
            console.error('=== ERROR SUBIENDO DOCUMENTO FRONTEND ===');
            console.error('Error completo:', error);
            console.error('Error response:', error.response);
            console.error('Error message:', error.message);
            toast.error(error.response?.data?.message || 'Error al subir el documento');
        } finally {
            setUploadingDocument(false);
        }
    };



    // Función para manejar la visibilidad de documentos
    // Función para manejar cambios en la visibilidad de documentos
    // documentType ahora es el ID del tipo de documento (BIGINT)
    const handleDocumentVisibilityChange = async (documentType, isVisible) => {
        try {
            // Llamar al backend para actualizar el estado obligatorio
            const response = await empleadosService.updateDocumentObligatory(
                empleadoId,
                documentType,
                isVisible
            );

            if (response.data.success) {
                // Actualizar el estado local solo si la llamada al backend fue exitosa
                setDocumentosVisibles(prev => ({
                    ...prev,
                    [documentType]: isVisible
                }));

                // Mostrar mensaje de confirmación
                toast.success(`Documento marcado como ${isVisible ? 'obligatorio' : 'no obligatorio'}`);

                // Verificar solo la validación del empleado sin recargar documentos
                await checkEmployeeValidationOnly();
            } else {
                // Si falló la llamada al backend, mostrar error
                toast.error(response.data.message || 'Error al actualizar el estado del documento');
            }
        } catch (error) {
            console.error('Error actualizando estado obligatorio del documento:', error);
            toast.error('Error al actualizar el estado del documento. Verifique la conexión.');

            // No actualizar el estado local si falló la llamada al backend
            // El checkbox permanecerá en su estado anterior
        }
    };

    // Función para manejar la selección/deselección de tarjetas de documentos
    // documentType ahora es el ID del tipo de documento (BIGINT)
    // Cambia el estado de selección de una tarjeta específica
    // Si está seleccionada, la deselecciona; si no está seleccionada, la selecciona
    const handleDocumentCardClick = (documentType) => {
        setDocumentosSeleccionados(prev => ({
            ...prev,
            [documentType]: !prev[documentType]
        }));
    };

    // Función para descargar documentos seleccionados
    // Solo permite descargar si hay al menos un documento seleccionado
    // Filtra solo los documentos que realmente tienen archivos asociados
    const handleDownloadSelected = async () => {
        if (!empleadoDetalle) {
            toast.warning('No hay datos del empleado disponibles');
            return;
        }

        // Obtener los documentos seleccionados con toda su información
        const documentosParaDescargar = Object.entries(documentosSeleccionados)
            .filter(([_, isSelected]) => isSelected)
            .map(([docType, _]) => {
                // Buscar el documento del empleado para este tipo
                const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc =>
                    doc.DOCUMENTO_TIPO_ID === docType.toString() && doc.FICHERO
                );
                return empleadoDoc;
            })
            .filter(doc => doc); // Filtrar documentos que no existen

        if (documentosParaDescargar.length === 0) {
            toast.warning('No hay documentos seleccionados con ficheros para descargar');
            return;
        }

        try {
            setLoading(true);
            toast.info(`Preparando descarga de ${documentosParaDescargar.length} documento(s) seleccionado(s)`);

            // Llamar al servicio para descargar los documentos seleccionados
            // Ahora enviamos los datos completos de los documentos
            const response = await empleadosService.downloadSelectedDocuments(empleadoId, documentosParaDescargar);

            // Generar nombre del archivo ZIP con datos del empleado
            const fecha = new Date();
            const dia = String(fecha.getDate()).padStart(2, '0');
            const mes = String(fecha.getMonth() + 1).padStart(2, '0');
            const anio = fecha.getFullYear();
            const fechaFormateada = `${dia}-${mes}-${anio}`;
            const nombreEmpleado = `${empleadoDetalle?.NOMBRE || ''}_${empleadoDetalle?.APELLIDOS || ''}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]/g, '');
            const filename = `documentos_empleado_${empleadoId}_${nombreEmpleado}_${fechaFormateada}.zip`;

            // Crear un enlace temporal para descargar el archivo ZIP
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Descarga completada: ${documentosParaDescargar.length} documento(s)`);

        } catch (error) {
            console.error('Error descargando documentos:', error);
            toast.error('Error al descargar los documentos seleccionados');
        } finally {
            setLoading(false);
        }
    };

    // Función para descargar todos los documentos
    // Descarga todos los documentos disponibles del empleado que tengan ficheros asociados
    const handleDownloadAll = async () => {
        if (!empleadoDetalle) {
            toast.warning('No hay datos del empleado disponibles');
            return;
        }

        // Obtener todos los documentos que tienen ficheros asociados
        const documentosConFicheros = empleadoDetalle?.documentosEmpleado?.filter(doc => doc.FICHERO) || [];

        if (documentosConFicheros.length === 0) {
            toast.warning('No hay documentos con ficheros para descargar');
            return;
        }

        try {
            setLoading(true);
            toast.info(`Preparando descarga de todos los documentos (${documentosConFicheros.length})`);

            // Llamar al servicio para descargar todos los documentos
            const response = await empleadosService.downloadAllDocuments(empleadoId);

            // Generar nombre del archivo ZIP con datos del empleado
            const fecha = new Date();
            const dia = String(fecha.getDate()).padStart(2, '0');
            const mes = String(fecha.getMonth() + 1).padStart(2, '0');
            const anio = fecha.getFullYear();
            const fechaFormateada = `${dia}-${mes}-${anio}`;
            const nombreEmpleado = `${empleadoDetalle?.NOMBRE || ''}_${empleadoDetalle?.APELLIDOS || ''}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]/g, '');
            const filename = `documentos_empleado_${empleadoId}_${nombreEmpleado}_${fechaFormateada}.zip`;

            // Crear un enlace temporal para descargar el archivo ZIP
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Descarga completada: ${documentosConFicheros.length} documento(s)`);

        } catch (error) {
            console.error('Error descargando todos los documentos:', error);
            toast.error('Error al descargar todos los documentos');
        } finally {
            setLoading(false);
        }
    };



    // Importar empleados
    const handleImportEmployees = async () => {
        try {
            setLoading(true);
            const response = await empleadosService.importEmployees();

            if (response.data.success) {
                toast.success(`Empleados importados correctamente.`);
                fetchEmpleados();
            } else {
                toast.error(response.data.message || "Error al importar empleados");
            }
        } catch (error) {
            console.error('Error importando empleados:', error);
            toast.error(
                error.response?.data?.message ||
                "Error al importar empleados. Verifique la conexión con la base de datos."
            );
        } finally {
            setLoading(false);
        }
    };

    // Función para guardar el seguimiento factorial
    const handleSaveSeguimientoFactorial = async () => {
        if (factoresSeguimiento.length === 0) {
            toast.warning('Debes seleccionar al menos un factor de seguimiento');
            return;
        }

        try {
            setLoading(true);

            // Concatenar los IDs de los factores seleccionados con ;
            const factoresIdsConcatenados = factoresSeguimiento.join(';');
            console.log('Factores de seguimiento a guardar:', factoresSeguimiento);
            console.log('IDs concatenados:', factoresIdsConcatenados);

            // Llamar al backend para guardar el seguimiento factorial
            const response = await empleadosService.saveSeguimientoFactorial(empleadoId, factoresIdsConcatenados);

            if (response.data.success) {
                toast.success('Seguimiento factorial guardado correctamente');
                console.log('Seguimiento factorial guardado:', response.data);
            } else {
                toast.error(response.data.message || 'Error al guardar el seguimiento factorial');
            }
        } catch (error) {
            console.error('Error guardando seguimiento factorial:', error);
            toast.error('Error al guardar el seguimiento factorial. Verifique la conexión.');
        } finally {
            setLoading(false);
        }
    };

    // Si hay un empleadoId, mostrar el detalle del empleado
    if (empleadoId && empleadoDetalle) {
        const empleadoData = isEditing ? empleadoEdit : empleadoDetalle;

        return (
            <>
                <Card className="mt-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(user?.ROL === 'admin' ? '/empleados' : '/empleado-detail')}
                                className="me-3"
                            >
                                <FaArrowLeft />
                            </Button>
                            <h4 className="mb-0">
                                {user?.ROL === 'admin' ? 'Detalle del Empleado' : 'Mi Perfil'}
                            </h4>
                        </div>
                        <div>
                            {!isEditing && user?.ROL === 'admin' ? (
                                <Button variant="primary" onClick={handleEdit}>
                                    Editar
                                </Button>
                            ) : isEditing && user?.ROL === 'admin' ? (
                                <div className="d-flex gap-2">
                                    <Button variant="success" onClick={handleSave} disabled={loading}>
                                        {loading ? "Guardando..." : "Guardar"}
                                    </Button>
                                    <Button variant="secondary" onClick={handleCancel}>
                                        Cancelar
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <h5 className="mb-5">Información Personal</h5>

                        {/* Fila del Estado y Fecha Importación - Ocupa toda la fila */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Estado:</strong></label>
                                    <Badge bg={empleadoData.ESTADO == 1 ? "success" : "warning"} className="ms-2">
                                        {formatEmployeeStatus(empleadoData.ESTADO)}
                                    </Badge>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Fecha Importación: {formatDate(empleadoData.FECHA_IMPORTACION)}</strong></label>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="">
                                    <Form.Check
                                        type="checkbox"
                                        id="accesoPlataforma"
                                        label="Acceso a la Plataforma"
                                        checked={empleadoData.ACCESO_PLATAFORMA === 1 || empleadoData.ACCESO_PLATAFORMA === true}
                                        onChange={(e) => {
                                            if (isEditing && user?.ROL === 'admin') {
                                                handleInputChange('ACCESO_PLATAFORMA', e.target.checked ? 1 : 0);
                                            }
                                        }}
                                        disabled={!isEditing || user?.ROL !== 'admin'}
                                        className=""
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="">
                                    <Form.Check
                                        type="checkbox"
                                        id="activo"
                                        label="Activo"
                                        checked={empleadoData.ACTIVO == 1 || empleadoData.ACTIVO == true}
                                        onChange={(e) => {
                                            if (isEditing && user?.ROL == 'admin') {
                                                handleInputChange('ACTIVO', e.target.checked ? 1 : 0);
                                            }
                                        }}
                                        disabled={!isEditing || user?.ROL !== 'admin'}
                                        className=""
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="">
                                    <Form.Check
                                        type="checkbox"
                                        id="enviarNotificaciones"
                                        label="Enviar notificaciones"
                                        checked={empleadoData.ENVIAR_NOTIFICACIONES == 1 || empleadoData.ENVIAR_NOTIFICACIONES == true}
                                        onChange={(e) => {
                                            if (isEditing && user?.ROL == 'admin') {
                                                handleInputChange('ENVIAR_NOTIFICACIONES', e.target.checked ? 1 : 0);
                                            }
                                        }}
                                        disabled={!isEditing || user?.ROL !== 'admin'}
                                        className=""
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Primera fila: Nombre, Apellidos, Email, DNI */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Nombre:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.NOMBRE || ''}
                                            onChange={(e) => handleInputChange('NOMBRE', e.target.value)}
                                            placeholder="Nombre"
                                        />
                                    ) : (
                                        <p>{empleadoData.NOMBRE}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Apellidos:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.APELLIDOS || ''}
                                            onChange={(e) => handleInputChange('APELLIDOS', e.target.value)}
                                            placeholder="Apellidos"
                                        />
                                    ) : (
                                        <p>{empleadoData.APELLIDOS}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Email:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="email"
                                            value={empleadoData.EMAIL || ''}
                                            onChange={(e) => handleInputChange('EMAIL', e.target.value)}
                                            placeholder="Email"
                                        />
                                    ) : (
                                        <p>{empleadoData.EMAIL}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>DNI:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.DNI || ''}
                                            onChange={(e) => handleInputChange('DNI', e.target.value)}
                                            placeholder="DNI"
                                        />
                                    ) : (
                                        <p>{empleadoData.DNI}</p>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* Segunda fila: Teléfono, Fecha de Alta, Número SS, Fecha Nacimiento */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Teléfono:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.TELEFONO_DEFECTO || ''}
                                            onChange={(e) => handleInputChange('TELEFONO_DEFECTO', e.target.value)}
                                            placeholder="Teléfono"
                                        />
                                    ) : (
                                        <p>{empleadoData.TELEFONO_DEFECTO}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Fecha de Alta Seguridad Social:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="date"
                                            value={formatDateForInput(empleadoData.FECHA_ALTA)}
                                            onChange={(e) => handleInputChange('FECHA_ALTA', e.target.value)}
                                        />
                                    ) : (
                                        <p>{formatDate(empleadoData.FECHA_ALTA)}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Número Seguridad Social:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.NUM_SS || ''}
                                            onChange={(e) => handleInputChange('NUM_SS', e.target.value)}
                                            placeholder="Número Seguridad Social"
                                        />
                                    ) : (
                                        <p>{empleadoData.NUM_SS}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Fecha de Nacimiento:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="date"
                                            value={formatDateForInput(empleadoData.FECHA_NACIMIENTO)}
                                            onChange={(e) => handleInputChange('FECHA_NACIMIENTO', e.target.value)}
                                        />
                                    ) : (
                                        <p>{formatDate(empleadoData.FECHA_NACIMIENTO)}</p>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* Tercera fila: Función Departamento, Contrato Horas, Relación Laboral, Horario */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Función del Departamento:</strong></label>
                                    {isEditing ? (
                                        <Form.Select
                                            value={empleadoData.DPTO_FUNCION || ''}
                                            onChange={(e) => handleInputChange('DPTO_FUNCION', e.target.value)}
                                        >
                                            <option value="">Seleccionar departamento</option>
                                            {departamentos.map((depto) => (
                                                <option key={depto.ID} value={depto.ID}>
                                                    {depto.NOMBRE}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <p>{departamentos.find(depto => depto.ID === empleadoData.DPTO_FUNCION)?.NOMBRE || empleadoData.DPTO_FUNCION}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Contrato Horas:</strong></label>
                                    {isEditing ? (
                                        <Form.Select
                                            value={empleadoData.CONTRATO_HORAS || ''}
                                            onChange={(e) => handleInputChange('CONTRATO_HORAS', e.target.value)}
                                        >
                                            <option value="">Seleccionar contrato</option>
                                            {contratosHoras.map((contrato) => (
                                                <option key={contrato.ID} value={contrato.ID}>
                                                    {contrato.TEXTO}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <p>{contratosHoras.find(contrato => contrato.ID === empleadoData.CONTRATO_HORAS)?.TEXTO || empleadoData.CONTRATO_HORAS}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Relación Laboral:</strong></label>
                                    {isEditing ? (
                                        <Form.Select
                                            value={empleadoData.RELACION_LABORAL || ''}
                                            onChange={(e) => handleInputChange('RELACION_LABORAL', e.target.value)}
                                        >
                                            <option value="">Seleccionar relación laboral</option>
                                            {relacionesLaborales.map((relacion) => (
                                                <option key={relacion.ID} value={relacion.ID}>
                                                    {relacion.TEXTO}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <p>{relacionesLaborales.find(relacion => relacion.ID === empleadoData.RELACION_LABORAL)?.TEXTO || empleadoData.RELACION_LABORAL}</p>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Horario:</strong></label>
                                    {isEditing ? (
                                        <Form.Control
                                            type="text"
                                            value={empleadoData.HORARIO || ''}
                                            onChange={(e) => handleInputChange('HORARIO', e.target.value)}
                                            placeholder="Horario"
                                        />
                                    ) : (
                                        <p>{empleadoData.HORARIO}</p>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* Cuarta fila: Protección Datos, Política RR LL, Acoso, Confidencialidad */}
                        {/* Campos de estado con valores bigint: 1='Sí', 2='No', 3='Enviado' */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Protección de Datos:</strong></label>
                                    {/* Campo bigint: 1='Sí', 2='No', 3='Enviado' */}
                                    <p className={getStatusFieldClass(empleadoData.PROT_DATOS)}>
                                        {formatStatusField(empleadoData.PROT_DATOS)}
                                    </p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Política RR LL:</strong></label>
                                    {/* Campo bigint: 1='Sí', 2='No', 3='Enviado' */}
                                    <p className={getStatusFieldClass(empleadoData.POLITICA_RR_LL)}>
                                        {formatStatusField(empleadoData.POLITICA_RR_LL)}
                                    </p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Acoso:</strong></label>
                                    {/* Campo bigint: 1='Sí', 2='No', 3='Enviado' */}
                                    <p className={getStatusFieldClass(empleadoData.ACOSO)}>
                                        {formatStatusField(empleadoData.ACOSO)}
                                    </p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="mb-3">
                                    <label className="form-label"><strong>Confidencialidad:</strong></label>
                                    {/* Campo bigint: 1='Sí', 2='No', 3='Enviado' */}
                                    <p className={getStatusFieldClass(empleadoData.CONFIDENCIALIDAD)}>
                                        {formatStatusField(empleadoData.CONFIDENCIALIDAD)}
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        {/* Panel de Seguimiento Factorial - Solo visible para administradores */}
                        {user?.ROL === 'admin' && (
                            <div className="mt-5 mb-5">
                                <h5 className="mb-3">Seguimiento Factorial</h5>
                                <div className="mb-3">
                                    <label className="form-label">
                                        <strong>Factores de Seguimiento:</strong>
                                    </label>

                                    {/* Multiselector personalizado */}
                                    <div className="position-relative factores-dropdown-container">
                                        {/* Botón del desplegable */}
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100 text-start d-flex justify-content-between align-items-center"
                                            onClick={() => setShowFactoresDropdown(!showFactoresDropdown)}
                                            style={{ minHeight: '40px' }}
                                        >
                                            <div className="d-flex flex-wrap gap-1 align-items-center flex-grow-1">
                                                {factoresSeguimiento.length === 0 ? (
                                                    <span className="text-muted">Selecciona factores de seguimiento...</span>
                                                ) : (
                                                    factoresSeguimiento.map((factorId) => {
                                                        const factor = tiposSeguimientoFactorial.find(f => f.ID === factorId);

                                                        return (
                                                            <span
                                                                key={factorId}
                                                                className="badge bg-primary d-flex align-items-center gap-1 me-1"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75em',
                                                                    padding: '0.35em 0.6em',
                                                                    lineHeight: '1.2'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // Evitar que se abra el dropdown
                                                                    setFactoresSeguimiento(prev => prev.filter(f => f !== factorId));
                                                                }}
                                                            >
                                                                {factor?.NOMBRE || `Factor ${factorId}`}
                                                                <span
                                                                    className="ms-1"
                                                                    style={{
                                                                        fontSize: '0.9em',
                                                                        fontWeight: 'bold',
                                                                        cursor: 'pointer',
                                                                        lineHeight: '1'
                                                                    }}
                                                                >
                                                                    ×
                                                                </span>
                                                            </span>
                                                        );
                                                    })
                                                )}
                                            </div>
                                            <i className={`fas fa-chevron-${showFactoresDropdown ? 'up' : 'down'}`}></i>
                                        </Button>

                                        {/* Dropdown con opciones */}
                                        {showFactoresDropdown && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                                {tiposSeguimientoFactorial.length > 0 ? (
                                                    tiposSeguimientoFactorial.map((factor) => (
                                                        <div
                                                            key={factor.ID}
                                                            className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const isSelected = factoresSeguimiento.includes(factor.ID);
                                                                if (isSelected) {
                                                                    setFactoresSeguimiento(prev => prev.filter(f => f !== factor.ID));
                                                                } else {
                                                                    setFactoresSeguimiento(prev => [...prev, factor.ID]);
                                                                }
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <div className="form-check me-2">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={factoresSeguimiento.includes(factor.ID)}
                                                                    onChange={() => { }} // Manejado por onClick del div
                                                                />
                                                            </div>
                                                            <span>{factor.NOMBRE}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-muted">
                                                        Cargando tipos de seguimiento...
                                                    </div>
                                                )}
                                            </div>
                                        )}


                                    </div>

                                    <div className="form-text">
                                        Haz clic en el desplegable para seleccionar múltiples factores de seguimiento
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <small className="text-muted">
                                            Factores seleccionados: <span className="badge bg-primary">{factoresSeguimiento.length}</span>
                                        </small>
                                    </div>
                                    <div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleSaveSeguimientoFactorial}
                                            disabled={loading || factoresSeguimiento.length === 0}
                                        >
                                            <i className="fas fa-save me-1"></i>
                                            {loading ? 'Guardando...' : 'Guardar Seguimiento'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tabla de control de visibilidad de documentos - Solo visible para administradores */}
                        {empleadoDetalle?.DPTO_FUNCION && user?.ROL === 'admin' && (
                            <div className="mt-5 mb-4">
                                <h5 className="mb-3">Documentos Obligatorios</h5>
                                <Card className="mb-4">
                                    <Card.Body>
                                        <Table striped bordered hover responsive>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px' }}>Obligatorio</th>
                                                    <th>Nombre del Documento</th>
                                                    <th>Estado / Fecha Validez</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tiposDocumentos.map((tipo) => {
                                                    console.log(`=== RENDERIZANDO FILA PARA TIPO ${tipo.ID} ===`);
                                                    console.log('empleadoDetalle:', empleadoDetalle);
                                                    console.log('documentosEmpleado:', empleadoDetalle?.documentosEmpleado);

                                                    const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc => {
                                                        console.log(`Comparando doc.DOCUMENTO_TIPO_ID (${doc.DOCUMENTO_TIPO_ID}) con tipo.ID.toString() (${tipo.ID.toString()})`);
                                                        return doc.DOCUMENTO_TIPO_ID === tipo.ID.toString();
                                                    });

                                                    console.log(`Documento encontrado para tipo ${tipo.ID}:`, empleadoDoc);
                                                    console.log(`Valor de HASTA:`, empleadoDoc?.HASTA);

                                                    return (
                                                        <tr key={tipo.ID}>
                                                            <td className="text-center">
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={documentosVisibles[tipo.ID] || false}
                                                                    onChange={(e) => handleDocumentVisibilityChange(tipo.ID, e.target.checked)}
                                                                    label=""
                                                                />
                                                            </td>
                                                            <td>{tipo.NOMBRE}</td>
                                                            <td>
                                                                {(() => {
                                                                    const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc =>
                                                                        doc.DOCUMENTO_TIPO_ID === tipo.ID.toString()
                                                                    );

                                                                    // Verificar si el documento no requiere renovación
                                                                    const esDocumentoSinRenovacion = tipo.DOCUMENTO_SIN_RENOVACION === true || tipo.DOCUMENTO_SIN_RENOVACION === 1;

                                                                    if (esDocumentoSinRenovacion) {
                                                                        // Documento sin renovación - mostrar estado del fichero
                                                                        if (empleadoDoc?.FICHERO) {
                                                                            return (
                                                                                <span className="text-success fw-bold">
                                                                                    Documento subido
                                                                                </span>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <span className="text-warning fw-bold">
                                                                                    Documento sin subir
                                                                                </span>
                                                                            );
                                                                        }
                                                                    } else if (empleadoDoc?.HASTA) {
                                                                        // Documento con renovación - mostrar fecha de validez
                                                                        const fechaValidez = new Date(empleadoDoc.HASTA);
                                                                        const fechaActual = new Date();
                                                                        const diferenciaDias = Math.ceil((fechaValidez - fechaActual) / (1000 * 60 * 60 * 24));
                                                                        const diasAvisoCaducaPronto = tipo.DIAS_AVISO_CADUCA_PRONTO ?? 30;

                                                                        // Determinar el color según la validez
                                                                        let colorValidez = '';

                                                                        if (diferenciaDias < 0) {
                                                                            // Documento vencido
                                                                            colorValidez = 'text-danger';
                                                                        } else if (diferenciaDias <= diasAvisoCaducaPronto) {
                                                                            // Documento vence dentro del umbral configurado para este tipo
                                                                            colorValidez = 'text-warning';
                                                                        } else {
                                                                            // Documento válido más allá del umbral configurado
                                                                            colorValidez = 'text-success';
                                                                        }

                                                                        return (
                                                                            <span className={`${colorValidez} fw-bold`}>
                                                                                {formatDate(empleadoDoc.HASTA)}
                                                                            </span>
                                                                        );
                                                                    } else {
                                                                        return '-';
                                                                    }
                                                                })()}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </div>
                        )}

                        {/* Panel de Documentos en formato de tarjetas */}
                        {/* Las tarjetas son clickeables y cambian de color azul a verde al seleccionarlas */}
                        {/* Solo mostrar si el empleado tiene una función del departamento seleccionada */}
                        {empleadoDetalle?.DPTO_FUNCION && (
                            <div className="mt-4 mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">Documentos</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge
                                            bg={Object.values(documentosSeleccionados).some(selected => selected) ? 'success' : 'secondary'}
                                            className="fs-6"
                                        >
                                            {Object.values(documentosSeleccionados).filter(selected => selected).length} de {Object.keys(documentosSeleccionados).length} documentos seleccionados
                                        </Badge>
                                        {Object.values(documentosSeleccionados).some(selected => selected) && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    const resetSeleccionados = {};
                                                    tiposDocumentos.forEach(tipo => {
                                                        resetSeleccionados[tipo.ID] = false;
                                                    });
                                                    setDocumentosSeleccionados(resetSeleccionados);
                                                }}
                                            >
                                                Deseleccionar Todos
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Botones de descarga */}
                                {/* Permiten descargar documentos seleccionados o todos los documentos disponibles */}
                                <div className="d-flex justify-content-end gap-2 mb-3">
                                    {/* Botón para descargar solo los documentos seleccionados */}
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleDownloadSelected}
                                        disabled={Object.values(documentosSeleccionados).filter(selected => selected).length === 0}
                                        title={Object.values(documentosSeleccionados).filter(selected => selected).length === 0 ?
                                            'Selecciona al menos un documento para descargar' :
                                            'Descargar documentos seleccionados'
                                        }
                                    >
                                        <FaDownload /> Descargar Seleccionados
                                    </Button>
                                    {/* Botón para descargar todos los documentos disponibles */}
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={handleDownloadAll}
                                        disabled={loading}
                                        title="Descargar todos los documentos disponibles"
                                    >
                                        <FaDownload /> Descargar Todo
                                    </Button>
                                </div>
                                <Row>
                                    {/* Tarjetas de documentos generadas dinámicamente */}
                                    {tiposDocumentos.map((tipo) => (
                                        <Col md={6} lg={4} className="mb-3" key={tipo.ID}>
                                            <Card
                                                className={`h-100 ${documentosSeleccionados[tipo.ID] ? 'border-success' : ''}`}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: documentosSeleccionados[tipo.ID] ? '#d4edda' : 'white',
                                                    borderColor: documentosSeleccionados[tipo.ID] ? '#28a745' : '#dee2e6',
                                                    transition: 'all 0.3s ease',
                                                    transform: documentosSeleccionados[tipo.ID] ? 'scale(1.02)' : 'scale(1)'
                                                }}
                                                onClick={() => handleDocumentCardClick(tipo.ID)}
                                                onMouseEnter={(e) => {
                                                    if (!documentosSeleccionados[tipo.ID]) {
                                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                        e.currentTarget.style.transform = 'scale(1.01)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!documentosSeleccionados[tipo.ID]) {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                    }
                                                }}
                                            >
                                                <Card.Header className={documentosSeleccionados[tipo.ID] ? 'bg-success text-white' : 'bg-primary text-white'}>
                                                    <h6 className="mb-0">{tipo.NOMBRE}</h6>
                                                </Card.Header>
                                                <Card.Body className="d-flex flex-column">
                                                    <div className="mb-3">
                                                        {(() => {
                                                            const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc => {
                                                                console.log(`Tarjeta: Buscando documento para tipo ${tipo.ID}:`, doc);
                                                                return doc.DOCUMENTO_TIPO_ID === tipo.ID.toString();
                                                            });
                                                            console.log(`Tarjeta: Documento encontrado para tipo ${tipo.ID}:`, empleadoDoc);
                                                            console.log(`Tarjeta: Valor de HASTA para tipo ${tipo.ID}:`, empleadoDoc?.HASTA);

                                                            // Verificar si el documento no requiere renovación
                                                            const esDocumentoSinRenovacion = tipo.DOCUMENTO_SIN_RENOVACION === true || tipo.DOCUMENTO_SIN_RENOVACION === 1;

                                                            if (esDocumentoSinRenovacion) {
                                                                // Documento sin renovación - mostrar estado del fichero
                                                                if (empleadoDoc?.FICHERO) {
                                                                    return (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong>Fichero:</strong> {empleadoDoc.FICHERO || 'Sin nombre'}
                                                                            </div>
                                                                            <div>
                                                                                <strong>Estado:</strong>
                                                                                <span className="ms-2 badge bg-success text-white">
                                                                                    Documento subido
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <div>
                                                                            <strong>Estado:</strong>
                                                                            <span className="ms-2 badge bg-warning text-white">
                                                                                Documento sin subir
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                            } else if (empleadoDoc?.HASTA) {
                                                                // Documento con renovación - mostrar fecha de validez
                                                                const fechaValidez = new Date(empleadoDoc.HASTA);
                                                                const fechaActual = new Date();
                                                                const diferenciaDias = Math.ceil((fechaValidez - fechaActual) / (1000 * 60 * 60 * 24));
                                                                const diasAvisoCaducaPronto = tipo.DIAS_AVISO_CADUCA_PRONTO ?? 30;

                                                                // Determinar el color según la validez
                                                                let colorValidez = '';
                                                                let textoValidez = '';

                                                                if (diferenciaDias < 0) {
                                                                    // Documento vencido
                                                                    colorValidez = 'text-danger';
                                                                    textoValidez = 'VENCIDO';
                                                                } else if (diferenciaDias <= diasAvisoCaducaPronto) {
                                                                    // Documento vence dentro del umbral configurado para este tipo
                                                                    colorValidez = 'text-warning';
                                                                    textoValidez = 'VENCE PRONTO';
                                                                } else {
                                                                    // Documento válido más allá del umbral configurado
                                                                    colorValidez = 'text-success';
                                                                    textoValidez = 'VÁLIDO';
                                                                }

                                                                return (
                                                                    <>
                                                                        <div className="mb-2">
                                                                            <strong>Fichero:</strong> {empleadoDoc.FICHERO || 'Sin nombre'}
                                                                        </div>
                                                                        <div>
                                                                            <strong>Fecha de Validez:</strong> {formatDate(empleadoDoc.HASTA)}
                                                                            <span className={`ms-2 badge ${colorValidez === 'text-danger' ? 'bg-danger' : colorValidez === 'text-warning' ? 'bg-warning' : 'bg-success'} text-white`}>
                                                                                {textoValidez}
                                                                            </span>
                                                                        </div>
                                                                        <div className={`mt-1 small ${colorValidez}`}>
                                                                            {diferenciaDias < 0
                                                                                ? `Vencido hace ${Math.abs(diferenciaDias)} día(s)`
                                                                                : diferenciaDias === 0
                                                                                    ? 'Vence hoy'
                                                                                    : `Vence en ${diferenciaDias} día(s)`
                                                                            }
                                                                        </div>
                                                                    </>
                                                                );
                                                            } else {
                                                                return (
                                                                    <div>
                                                                        <strong>Fecha de Validez:</strong> Sin fecha
                                                                    </div>
                                                                );
                                                            }
                                                        })()}
                                                    </div>
                                                    <div className="mt-auto">
                                                        <div className="d-grid gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleUploadDocument(tipo.ID);
                                                                }}
                                                            >
                                                                <FaUpload /> Subir fichero
                                                            </Button>
                                                            {/* Botón Eliminar Fichero - solo visible cuando hay fichero subido */}
                                                            {(() => {
                                                                const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc =>
                                                                    doc.DOCUMENTO_TIPO_ID === tipo.ID.toString()
                                                                );
                                                                // Comprobar si tiene fichero subido para mostrar el botón Eliminar
                                                                return empleadoDoc?.FICHERO;
                                                            })() && (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteDocument(tipo.ID);
                                                                        }}
                                                                    >
                                                                        <FaTrash /> Eliminar Fichero
                                                                    </Button>
                                                                )}

                                                            {/* Botón Enviar - solo visible cuando NO hay fichero subido */}
                                                            {(() => {
                                                                const empleadoDoc = empleadoDetalle?.documentosEmpleado?.find(doc =>
                                                                    doc.DOCUMENTO_TIPO_ID === tipo.ID.toString()
                                                                );
                                                                // Comprobar si NO tiene fichero subido para mostrar el botón Enviar
                                                                return (!empleadoDoc?.FICHERO && tipo.ENVIO_PLANTILLA == 1);
                                                            })() && (
                                                                    <Button
                                                                        variant="outline-success"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSendDocument(tipo.ID);
                                                                        }}
                                                                    >
                                                                        <FaPaperPlane /> Enviar
                                                                    </Button>
                                                                )}
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                    </Card.Body>
                </Card>

                {/* Modal para subir archivo */}
                <Modal show={showUploadModal} onHide={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setValidityDate('');
                }} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Subir Documento</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {/* Información sobre el cálculo automático de la fecha */}
                        {currentDocumentType && (() => {
                            const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                            if (tipoDocumento) {
                                // Si el documento no tiene renovación, mostrar mensaje específico
                                if (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1) {
                                    return (
                                        <div className="alert alert-warning mb-3">
                                            <small>
                                                <strong>Información:</strong> Este documento no tiene fecha de caducidad.
                                                <br />
                                                <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                                            </small>
                                        </div>
                                    );
                                }
                                // Si tiene meses hasta próxima renovación, mostrar información de cálculo automático
                                else if (tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                                    return (
                                        <div className="alert alert-info mb-3">
                                            <small>
                                                <strong>Información:</strong> La fecha de validez se calculará automáticamente sumando {tipoDocumento.MESES_HASTA_PROX_RENOVACION} mes(es) a la fecha actual.
                                                <br />
                                                <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                                            </small>
                                        </div>
                                    );
                                }
                                // Si no tiene meses de renovación configurados
                                else {
                                    return (
                                        <div className="alert alert-warning mb-3">
                                            <small>
                                                <strong>Información:</strong> No hemos podido encontrar una fecha de Validez apta para el calculo de la fecha.
                                                <br />
                                                <strong>Tipo de documento:</strong> {tipoDocumento.NOMBRE}
                                            </small>
                                        </div>
                                    );
                                }
                            }
                            return null;
                        })()}

                        <div className="mb-3">
                            <label className="form-label">Seleccionar archivo PDF:</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".pdf"
                                onChange={handleFileSelect}
                                disabled={uploadingDocument}
                            />
                            <small className="text-muted">
                                Solo archivos PDF, máximo 10MB
                            </small>
                        </div>
                        {selectedFile && (
                            <div className="alert alert-info">
                                <strong>Archivo seleccionado:</strong> {selectedFile.name}
                                <br />
                                <strong>Tamaño:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                        )}

                        {/* Información de éxito y fecha de validez - solo visible después de seleccionar archivo */}
                        {selectedFile && (() => {
                            const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                            const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

                            return (
                                <div className="alert alert-info">
                                    <h6>
                                        {esDocumentoSinRenovacion
                                            ? 'Información del documento:'
                                            : 'Se ha detectado la Validez del documento hasta:'
                                        }
                                    </h6>

                                    {/* Solo mostrar campo de fecha si el documento requiere renovación */}
                                    {!esDocumentoSinRenovacion ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Fecha de Caducidad:
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={validityDate}
                                                onChange={(e) => setValidityDate(e.target.value)}
                                                required={true}
                                                isInvalid={!validityDate}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Por favor selecciona una fecha de validez
                                            </Form.Control.Feedback>
                                            <Form.Text className="text-muted">
                                                {currentDocumentType && (() => {
                                                    if (tipoDocumento && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== null && tipoDocumento.MESES_HASTA_PROX_RENOVACION !== undefined) {
                                                        return 'Puedes modificar la fecha calculada automáticamente si es necesario.';
                                                    } else {
                                                        return 'La fecha mostrada es la fecha actual. Puedes modificarla según sea necesario.';
                                                    }
                                                })()}
                                            </Form.Text>
                                        </Form.Group>
                                    ) : (
                                        <div className="alert alert-success mb-3">
                                            <small>
                                                <strong>Información:</strong> Este documento no requiere fecha de caducidad.
                                                <br />
                                                Se subirá sin fecha de validez.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        {(() => {
                            const tipoDocumento = tiposDocumentos.find(tipo => tipo.ID === currentDocumentType);
                            const esDocumentoSinRenovacion = tipoDocumento && (tipoDocumento.DOCUMENTO_SIN_RENOVACION === true || tipoDocumento.DOCUMENTO_SIN_RENOVACION === 1);

                            return (
                                <>
                                    <Button variant="secondary" onClick={() => {
                                        setShowUploadModal(false);
                                        setSelectedFile(null);
                                        setValidityDate(null);
                                    }} disabled={uploadingDocument}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleUploadFile}
                                        disabled={!selectedFile || (!validityDate && !esDocumentoSinRenovacion) || uploadingDocument}
                                    >
                                        {uploadingDocument ? 'Subiendo...' : 'Subir Documento y Guardar Fecha'}
                                    </Button>
                                </>
                            );
                        })()}
                    </Modal.Footer>
                </Modal>

            </>
        );
    }

    // Si no hay empleadoId, mostrar la lista de empleados

};

export default EmpleadoDetail; 